import { useEffect, useRef } from 'react';
import { useLazyQuery } from '@apollo/client/react/hooks';
import { Organization } from '@oolio-group/domain';
import { GET_ORGANIZATION_DETAILS } from '../../graphql/organization';
import { sessionSubject } from '../../state/sessionObservable';
import { setSession } from '../../state/preferences';
import useBehaviorSubjectState from '../rxjs/useSubjectState';

/**
 * Device-code login only returns the organization's id and country, so a CDS
 * paired that way has no organization name in its session. The audit service
 * rejects an upload when the org name is empty, so fetch and persist it.
 *
 * The same fetch carries showOolioBranding, which gates the attribution on the
 * idle screen. That setting can change in the accounts portal at any time, so
 * the fetch runs once per organization rather than only until the session looks
 * complete. The guard is keyed on the organization id and cleared on logout, so
 * a re-pair fetches again whether or not the new organization differs. The
 * persisted session still serves an offline launch.
 */
export const useOrgSync = () => {
  const { value: session } = useBehaviorSubjectState(sessionSubject);
  const fetchedOrgIdRef = useRef<string>();

  const [fetchOrganization] = useLazyQuery<{
    getOrganizationById: Organization;
  }>(GET_ORGANIZATION_DETAILS, {
    fetchPolicy: 'no-cache',
    onCompleted: data => {
      const organization = data?.getOrganizationById;
      if (!organization?.name) return;
      const currentSession = sessionSubject.getValue();
      if (!currentSession?.currentOrganization) return;
      // A logout and re-pair can land while this request is in flight, so drop
      // a response that no longer matches the session's organization.
      if (currentSession.currentOrganization.id !== organization.id) return;

      const updatedSession = {
        ...currentSession,
        currentOrganization: {
          ...currentSession.currentOrganization,
          name: organization.name,
          country: organization.country,
          currencyCode: organization.currencyCode,
          // A null flag means the accounts subgraph could not answer. Keep the
          // last known value instead of persisting the gap.
          ...(typeof organization.showOolioBranding === 'boolean' && {
            showOolioBranding: organization.showOolioBranding,
          }),
        },
      };
      sessionSubject.next(updatedSession);
      setSession(updatedSession);
    },
  });

  useEffect(() => {
    const orgId = session?.currentOrganization?.id;
    if (!session?.authorized || !orgId) {
      fetchedOrgIdRef.current = undefined;
      return;
    }
    if (fetchedOrgIdRef.current === orgId) return;
    fetchedOrgIdRef.current = orgId;
    fetchOrganization({ variables: { orgId } });
  }, [
    session?.authorized,
    session?.currentOrganization?.id,
    fetchOrganization,
  ]);
};
